import { gql } from '@apollo/client';
import { useSubscription } from '@apollo/client';
import React, { useCallback, useEffect, useState } from 'react';

import { GenerateInvoiceXslJobContext } from './context';
import { useResettableMutation } from '../../hooks/useResettableMutation';

const START_JOB = gql`
  mutation startInvoiceXslJob($invoiceId: Int!, $groupBy: ReportGroupBy!) {
    generateInvoiceXsl(invoiceId: $invoiceId, groupBy: $groupBy)
  }
`;

const ON_GENERATE_INVOICE_XSL_JOB_PROGRESS = gql`
  subscription jobProgress($jobId: ID!) {
    job: generateInvoiceXslJobProgress(jobId: $jobId) {
      jobId
      filename
      url
      finished
      message
      errors
    }
  }
`;

export default function GenerateInvoiceXslJobProvider({ children }) {
  const [jobId, setJobId] = useState(null);
  const [jobErrors, setJobErrors] = useState([]);
  const [finished, setJobFinished] = useState(true);
  const [message, setMessage] = useState(null);
  const [filename, setFilename] = useState(null);
  const [url, setUrl] = useState(null);

  const clearJobId = useCallback(() => {
    setJobId(null);
    setJobErrors([]);
  }, []);

  useEffect(() => {
    if (!!jobId && !!finished) {
      setTimeout(() => clearJobId(), 10000);
    }
  }, [finished, clearJobId, jobId]);

  const [startInvoiceXslJob, { loading, error, reset }] = useResettableMutation(START_JOB, {
    onCompleted: data => {
      setJobFinished(false);
      setJobId(data?.generateInvoiceXsl);
    },
    onError: () => setTimeout(() => reset(), 10000)
  });

  const startGenerateInvoiceXslJob = data => {
    setJobFinished(false);
    startInvoiceXslJob({ variables: { invoiceId: data?.invoiceId, groupBy: data?.groupBy } });
  };

  useSubscription(ON_GENERATE_INVOICE_XSL_JOB_PROGRESS, {
    skip: !jobId,
    variables: { jobId },
    onSubscriptionData: ({ subscriptionData: { data } }) => {
      const job = data?.job;
      const { message, filename, url, finished, errors } = job;

      setJobErrors(errors);
      setJobFinished(finished);
      setMessage(message);
      setFilename(filename);
      setUrl(url);
    }
  });

  return (
    <GenerateInvoiceXslJobContext.Provider
      value={{
        startGenerateInvoiceXslJob,
        jobId,
        clearJobId,
        loading,
        error,
        finished,
        filename,
        url,
        message,
        jobErrors
      }}
    >
      {children}
    </GenerateInvoiceXslJobContext.Provider>
  );
}
